import { Box } from '@mui/material';
import isEqual from 'lodash.isequal';
import { lazy, Suspense } from 'react';
import type { DataPoint } from '@/features/chartsView';
import { LineChartWrapper, useFetchData } from '@/features/chartsView';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import { Loader } from '@/shared/ui';
import { CHART_BASE_DIMENSION } from '../../model/constants';
import {
  getData,
  getDataRequestedConfig,
  selectConfig,
  selectData,
  selectDimensionIsMedia,
  selectMeta,
  isRequestedConfigExist,
  selectDimensionIsDate,
} from '../../model/slice';
import { useChartParams } from '../AreaChart/useChartParams';

const AreaChart = lazy(() =>
  import('../AreaChart/AreaChart').then(({ AreaChart }) => {
    return {
      default: AreaChart,
    };
  }),
);

export function Chart() {
  const dispatch = useAppDispatch();
  const meta = useAppSelector(selectMeta);
  const chartConfig = useAppSelector(selectConfig)!;
  const dimensionIsMedia = useAppSelector(selectDimensionIsMedia);
  const dimensionIsDate = useAppSelector(selectDimensionIsDate);
  const chartData = useAppSelector(selectData);
  const splitOptions =
    chartData.data?.splitOptions?.[chartConfig.dimension] || [];
  const { noData, lines, yAxis, data } = useChartParams({
    config: meta?.config,
    metric: chartConfig.metric,
    data: (chartData.data?.data || []) as DataPoint<
      typeof CHART_BASE_DIMENSION
    >[],
    splitOptions,
    excludedSplitOptions: chartConfig.excludedSplitOptions,
  });

  useFetchData({
    isDataAndCurrentConfigEqual: isEqual(
      isRequestedConfigExist(chartData) ? chartData.requestedConfig : null,
      getDataRequestedConfig(chartConfig),
    ),
    onGetData: () => dispatch(getData(chartConfig)),
  });

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <LineChartWrapper
        dataStatus={chartData.status}
        noData={noData}
        noSelection={lines.length === chartConfig.excludedSplitOptions.length}
      >
        <Suspense fallback={<Loader active />}>
          <AreaChart
            excludedLines={chartConfig.excludedSplitOptions}
            isMediaContent={dimensionIsMedia}
            isDateContent={dimensionIsDate}
            xAxisDataKey={CHART_BASE_DIMENSION}
            yAxis={yAxis!}
            data={data}
            lines={lines}
            isRelative={chartConfig.showRelativeValues}
          />
        </Suspense>
      </LineChartWrapper>
    </Box>
  );
}
